<template>
    <wrapper-features-templates-operations
    :applyErrorState="applyErrorState && !showLoader"
    :activeStep="activeStep"
    :feedbackOperationData="headerOperationData"
    :scrollValue="scrollValue"
    :stepperParams="stepperParams"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :showStepper="activeStep <= stepperParams.length && !applyErrorState && !showLoader && !isAccountsEmpty"
    :showFooter="activeStep < 6 && !showLoader && !applyErrorState && !isAccountsEmpty"
    :footerButtonsParams="footerButtonsParams.filter(obj => !obj.hidden)"
    :tokenTitleAppend="t('bankassurance.subscribe.travel.tokenTitleAppend')"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, validateStep5Inputs)"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Insert content -->
        <component
        :is="getActiveStepComponent"
        :isMobile="getMobileAppState"
        :accountsBalances="getAccountsBalances"
        :clearFormErrors="clearFormErrors"
        :formData="formData"
        :isHolder="isHolder"
        :mandatoryList="mandatoryList[`step${activeStep}MandatoryList`]"
        :showMandatoryErrors="showMandatoryErrors"
        :showInsuredPersonErrors="showInsuredPersonErrors"
        :responseData="responseData"
        @formDataChange="formDataChangeHandler"
        @insuredPersonDataChange="insuredPersonDataChangeHandler"
        @formErrorsCleared="clearFormErrors = false"
        @changeHolder="isHolder = !isHolder"
        @addStepValidationError="addStepValidationError"
        @removeStepValidationError="removeStepValidationError"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @resetShowInsuredPersonErrorsProp="showInsuredPersonErrors = false"
        @beneficiaryDataChange="beneficiaryDataChangeHandler"
        @addBeneficiary="formData.beneficiaryList.push({ name: '' })"
        @removeBeneficiary="formData.beneficiaryList.splice($event, 1)"
        @scrollTo="scrollValue = $event"
        @showOverlayLoader="showOverlayLoader = true"
        @hideOverlayLoader="showOverlayLoader = false"
        @showError="errorHandler"/>
        <!-- New Subscription Button -->
        <button-primary
        v-if="!showSpinner && !applyErrorState && activeStep === 6"
        buttonSize="medium"
        icon="shield"
        iconPosition="right"
        :label="t('bankassurance.subscribe.travel.newSubscription')"
        :class="{'mobile': getMobileAppState}"
        @clicked="clearInputs()"/>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import ToastsGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount, watch } from 'vue'

/* Assets & Helpers */
import { BANKASSURANCE_PRODUCTS } from '@/assets/constants/core'
import { atLeastOneIsEmpty, clearObject } from '@/assets/scripts/objects'
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'
import { errorObjectConstructor } from '@/assets/scripts/toastsHelpers'
import moment from '@/assets/setups/moment'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {
    requestAccountsBalances, requestInsuranceHolder, requestInsuranceDomainsList,
    submitSimulation, acceptSimulation, submitProposal, clearFeatureStates
} = mapActions(moduleMapper.BANKASSURANCE_SUBSCRIBE)
const {	getSimulationData, getAccountsBalances, getDomainData } = mapGetters(moduleMapper.BANKASSURANCE_SUBSCRIBE)

const { getMobileAppState } = mapGetters()

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)

/* Data */
const applyErrorState = ref(false)
const isAccountsEmpty = ref(false)

/*************************************************
*                                                *
*                    Steps Data                  *
*                                                *
*************************************************/
const activeStep = ref(0)
const stepperParams = ref([
    { description: t('bankassurance.subscribe.travel.stepper.data') },
    { description: t('bankassurance.subscribe.travel.stepper.prizes') },
    { description: t('bankassurance.subscribe.travel.stepper.info') },
    { description: t('bankassurance.subscribe.travel.stepper.resume') },
    { description: t('bankassurance.subscribe.travel.stepper.payment') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/bankassurance/subscribe/travel/Step' + stepNumber))
})

/*************************************************
*                                                *
*                Form Logic Data                 *
*                                                *
*************************************************/
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const showInsuredPersonErrors = ref(false)
const clearFormErrors = ref(false)
const isHolder = ref(false)

/* The params are as the MDW receives them even tho they are not in English */
const formData = ref({
    originAccount: '',
    currency: '',
    dataInicio: '',
    dataFim: '',
    numPessoasSeguras: 1,
    planoProtecaoViagem: '',
    escalaoCapitalViagem: '',
    covidViagem: 'N',
    origemViagem: '008',
    destinoViagem: '',
    insuredPersonList: [],
    beneficiaryList: [],
    canProceed: false
})

const step1MandatoryList = ref([
    'dataInicio',
    'dataFim',
    'planoProtecaoViagem',
    'escalaoCapitalViagem'
])

const step3MandatoryList = ref([
    'destinoViagem'
])

const mandatoryList = ref({
    step1MandatoryList: step1MandatoryList.value,
    step3MandatoryList: step3MandatoryList.value
})

const formDataChangeHandler = ({ field, inputValue }) => {
    if (activeStep.value === 1) {
        isSimulationAccepted.value = false

        isHolder.value = false

        formData.value.destinoViagem = ''
        formData.value.insuredPersonList = []
        formData.value.beneficiaryList = []
    }

    formData.value[field] = inputValue
}

const insuredPersonDataChangeHandler = ({ field, subField, inputValue, index }) => {
    if (subField) {
        formData.value.insuredPersonList[index].insuranceEntityAddress[subField] = inputValue
    } else formData.value.insuredPersonList[index][field] = inputValue
}

let stepValidationErrors = []
const addStepValidationError = param => {
    if (!stepValidationErrors.find(ele => ele === param)) stepValidationErrors.push(param)
}

const removeStepValidationError = param => {
    if (stepValidationErrors.find(ele => ele === param)) stepValidationErrors.splice(stepValidationErrors.findIndex(ele => ele === param), 1)
}

const beneficiaryDataChangeHandler = ({ field, inputValue, index }) => {
    formData.value.beneficiaryList[index][field] = inputValue
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const clearInputs = () => {
    clearFormErrors.value = true
    stepValidationErrors = []
    clearObject(formData.value)

    formData.value.numPessoasSeguras = 1
    formData.value.covidViagem = 'N'
    formData.value.origemViagem = '008'
    formData.value.insuredPersonList = []
    formData.value.beneficiaryList = []

    /* Setting Initial Selected Account and Currency */
    formData.value.originAccount = getAccountsBalances.value.find(account => { return account.default }).id
    formData.value.currency = getAccountsBalances.value.find(account => { return account.default }).currency

    headerOperationData.value = null
    formData.value.canProceed = false
    activeStep.value = 1
}

const validateInputs = () => {
    switch (activeStep.value) {
        case 1:
            validateStep1Inputs()
            break
        case 2:
            validateStep2Inputs()
            break
        case 3:
            validateStep3Inputs()
            break
        case 4:
            showMandatoryErrors.value = false
            activeStep.value = 5
            break
        case 5:
            validateStep5Inputs()
            break
    }
}

const validateStep1Inputs = async () => {
    if (atLeastOneIsEmpty(formData.value, mandatoryList.value[`step${activeStep.value}MandatoryList`])) {
        showMandatoryErrors.value = true
        return
    }

    if (!isSimulationAccepted.value) {
        try {
            showOverlayLoader.value = true

            await submitSimulation({
                productCode: BANKASSURANCE_PRODUCTS.TRAVEL,
                data: {
                    params: {
                        dataInicio: moment(formData.value.dataInicio).format('DD/MM/YYYY'),
                        dataFim: moment(formData.value.dataFim).format('DD/MM/YYYY'),
                        planoProtecaoViagem: formData.value.planoProtecaoViagem,
                        escalaoCapitalViagem: formData.value.escalaoCapitalViagem,
                        covidViagem: formData.value.covidViagem,
                        numPessoasSeguras: formData.value.numPessoasSeguras
                    }
                }
            })

            showOverlayLoader.value = false

            activeStep.value = 2
        } catch (error) {
            errorHandler(error.response.data, validateStep1Inputs)
        }
    }
}

const isSimulationAccepted = ref(false)
const validateStep2Inputs = async () => {
    if (!isSimulationAccepted.value) {
        try {
            showOverlayLoader.value = true

            await acceptSimulation(getSimulationData.value.insuranceId)

            isSimulationAccepted.value = true
            showOverlayLoader.value = false
        } catch (error) {
            errorHandler(error.response.data, validateStep2Inputs)
        }
    }

    while (formData.value.insuredPersonList.length < formData.value.numPessoasSeguras) {
        formData.value.insuredPersonList.push({
            name: '',
            birthDate: '',
            identification: '',
            fiscalIdentification: '',
            contact: '',
            secondaryMobile: '',
            email: '',
            gender: '',
            insuranceEntityAddress: {
                province: '',
                postalCode: '',
                country: 'ANG'
            }
        })
    }

    activeStep.value = 3
}

const validateStep3Inputs = () => {
    if (stepValidationErrors.length) return

    formData.value.insuredPersonList.forEach(person => {
        if (!person.name.length ||
            !person.birthDate.length ||
            !person.identification.length ||
            !person.fiscalIdentification.length ||
            !person.contact.length ||
            !person.insuranceEntityAddress.province.length ||
            !person.insuranceEntityAddress.postalCode.length ||
            !person.insuranceEntityAddress.country.length) {
            showInsuredPersonErrors.value = true
        }
    })

    if (atLeastOneIsEmpty(formData.value, mandatoryList.value[`step${activeStep.value}MandatoryList`])) {
        showMandatoryErrors.value = true
        return
    }

    if (showInsuredPersonErrors.value) return

    formData.value.beneficiaryList = formData.value.beneficiaryList.filter(element => element.name.length)

    activeStep.value = 4
}

const validateStep5Inputs = async () => {
    if (!formData.value.canProceed) {
        showMandatoryErrors.value = true
        return
    }

    showOverlayLoader.value = true

    try {
        await submitProposal({
            product: BANKASSURANCE_PRODUCTS.TRAVEL,
            insuranceId: getSimulationData.value.insuranceId,
            data: {
                productCode: BANKASSURANCE_PRODUCTS.TRAVEL, // adding this so MDW can figure which product is being submited
                params: {
                    origemViagem: formData.value.origemViagem,
                    destinoViagem: formData.value.destinoViagem
                },
                insuredPersonList: [
                    ...formData.value.insuredPersonList.map(ele => {
                        return {
                            ...ele,
                            mobile: ele.contact,
                            gender: {
                                code: ele.gender,
                                value: getDomainData.value('Gender').find(el => el.code === ele.gender).description
                            },
                            insuranceEntityAddress: {
                                postalCode: {
                                    code: ele.insuranceEntityAddress.postalCode,
                                    value: getDomainData.value('Province').find(el => el.code === ele.insuranceEntityAddress.postalCode).description
                                },
                                province: {
                                    code: ele.insuranceEntityAddress.province,
                                    value: getDomainData.value('City').find(el => el.code === ele.insuranceEntityAddress.province).description
                                },
                                country: {
                                    code: ele.insuranceEntityAddress.country,
                                    value: getDomainData.value('AddressCountry').find(el => el.code === ele.insuranceEntityAddress.country).description
                                }
                            }
                        }
                    })
                ],
                beneficiaryList: [...formData.value.beneficiaryList],
                debitAccount: formData.value.originAccount
            }
        })

        showOverlayLoader.value = false
    } catch (error) {
        errorHandler(error.response.data, validateStep5Inputs)
    }
}

/* Footer Buttons Data */
const footerButtonsParams = computed(() => {
    return [{
        label: t('bankassurance.subscribe.travel.footerButtons.clear'),
        callbackFunction: clearInputs,
        buttonPosition: 'left',
        hidden: activeStep.value !== 1
    }, {
        label: t('bankassurance.subscribe.travel.footerButtons.goBack'),
        icon: 'arrow-left-full',
        callbackFunction: () => { activeStep.value-- },
        hidden: activeStep.value === 1
    }, {
        label: t('bankassurance.subscribe.travel.footerButtons.forward'),
        callbackFunction: validateInputs,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        hidden: activeStep.value === 5
    }, {
        label: t('bankassurance.subscribe.travel.footerButtons.validate'),
        callbackFunction: validateInputs,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        hidden: activeStep.value !== 5
    }]
})

/*************************************************
*                                                *
*                Operation Status                *
*                                                *
*************************************************/
const buttonsTopData = ref({})
const headerOperationData = ref(null)
const responseData = ref()

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref({
	toastType: '',
	text: '',
	buttons: []
})

const successHandler = data => {
    responseData.value = data

    headerOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: !data.data.hasReceipt ? null : {
            iconsColor: data.errorLevel === 'INFO' ? 'var(--positive)' : 'var(--warning)',
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: data.data.operationId },
            downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: data.data.operationId } }),
            operationId: data.data.operationId
        }
    }

    activeStep.value = 6
}

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { activeStep.value = 3 })

    showLoader.value = false
    showOverlayLoader.value = false
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*                Initial Requets                 *
*                                                *
*************************************************/
const initialRequests = async () => {
    applyErrorState.value = false
    isAccountsEmpty.value = false
	showLoader.value = true

    /* We are using a Promise all to save sometime, calling all api's at the same time, since they dont depend on eachother */
    Promise.all([
        requestAccountsBalances(),
        requestInsuranceHolder(),
        requestInsuranceDomainsList({ domain: 'TravelProtectionPlan' }),
        requestInsuranceDomainsList({ domain: 'TravelCapitalSscale' }),
        requestInsuranceDomainsList({ domain: 'Gender' }),
        requestInsuranceDomainsList({ domain: 'Province' }),
        requestInsuranceDomainsList({ domain: 'AddressCountry' }),
        requestInsuranceDomainsList({ domain: 'Country' })
    ]).then(() => {
        if (getAccountsBalances.value.length > 0) {
            /* Setting Initial Selected Account and Currency */
            formData.value.originAccount = getAccountsBalances.value.find(account => { return account.default }).id
            formData.value.currency = getAccountsBalances.value.find(account => { return account.default }).currency
        } else {
            isAccountsEmpty.value = true
        }

        showLoader.value = false
        activeStep.value = 1
    }).catch(error => {
        applyErrorState.value = true
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, initialRequests)
    })
}

watch(() => activeStep.value, (newVal, oldVal) => {
    scrollValue.value = 0
})

initialRequests()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.button-primary {
    min-width: 200px;

    &.mobile {
        width: 100%;
    }
}
</style>
